import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { AuthService } from '../../features/auth/services/auth.service';
import { NotificationStateService } from '../../core/services/notification-state.service';
import { PermissionService } from '../../core/services/permission.service';
import { NotificationsService } from '../../features/Notifications/services/notifications.service';
import { RequestCountService } from '../../features/request/service/request-count.service';
import { RealtimeService } from '../../core/services/realtime.service';
import { SecureStorageService } from '../../core/services/secure-storage.service';
import { RoutePrefetchService } from '../../core/services/route-prefetch.service';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { BreadcrumbComponent } from '../../core/components/breadcrumb/breadcrumb.component';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, NzModalModule, BreadcrumbComponent],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.css',
})
export class LayoutComponent implements OnDestroy, AfterViewInit {
  @ViewChild('sidebarScrollContainer') sidebarScrollContainer?: ElementRef<HTMLElement>;
  rowData: any[] = [];
  notificationCount: number = 0;
  requestCount: number = 0;
  private requestCountSub?: Subscription;
  logoutModalVisible = false;
  userName = 'Hi,DigiContests';
  userEmail = '';
  userRole = 'Admin';
  userInitials = 'U';
  // Track expanded menus
  expandedMenus: { [key: string]: boolean } = {};
  isSidebarCollapsed = false;
  isMobileSidebarOpen = false;
  sidebarTooltipVisible = false;
  sidebarTooltipText = '';
  sidebarTooltipLeft = 0;
  sidebarTooltipTop = 0;
  collapsedHoverMenuKey: string | null = null;
  isSidebarScrolling = false;
  private collapsedHoverCloseTimer: any = null;
  private sidebarScrollTop = 0;
  private scrollHideTimer: any = null;

  /** What the user typed into the sidebar search box. */
  menuSearch = '';

  /**
   * Flat index of every sidebar destination, used only by the search box.
   * The menu tree itself stays hand-written markup — this list just mirrors
   * the labels, routes and permission keys already used there, so a match can
   * be rendered as a single flat result row.
   */
  private readonly searchableMenu: Array<{
    label: string;
    route: string;
    group?: string;
    queryParams?: any;
    permission?: string;
    superAdmin?: boolean;
    loyalty?: boolean;
  }> = [
    { label: 'Dashboard', route: '/dashboard', permission: 'dashboard' },
    { label: 'AI Insights', route: '/ai-insights', permission: 'ai-insights' },

    { label: 'Campaigns', route: '/contests', group: 'Content', permission: 'contest' },
    { label: 'Quiz', route: '/quiz', group: 'Content', permission: 'quiz' },
    { label: 'Task Management', route: '/task-management', group: 'Content', permission: 'task-management' },
    { label: 'Prize Categories', route: '/categories', group: 'Content', permission: 'categories' },

    { label: 'Participants', route: '/users', group: 'User Management', permission: 'users' },
    { label: 'Clients', route: '/clients', group: 'User Management', permission: 'client' },
    { label: 'Client QR', route: '/client-qr', group: 'User Management', permission: 'client' },
    { label: 'Branding', route: '/branding', group: 'User Management', permission: 'client' },

    {
      label: 'Notification',
      route: '/notification',
      group: 'Communication',
      queryParams: { status: 'history' },
      permission: 'notification',
    },
    { label: 'Newsletter', route: '/newsletter', group: 'Communication', permission: 'newsletter' },
    { label: 'Feedback', route: '/feedback', group: 'Communication', permission: 'feedback' },
    { label: 'Request', route: '/request', group: 'Communication', permission: 'request' },
    { label: 'Email Templates', route: '/email-templates', group: 'Communication', permission: 'email-templates' },

    { label: 'Analysis & Report', route: '/analysis-reports', permission: 'analysis-reports' },

    { label: 'Settings', route: '/setting', group: 'Settings & Config', permission: 'policies' },
    { label: 'Subscription', route: '/subscription', group: 'Settings & Config', permission: 'subscription' },
    { label: 'CMS', route: '/terms-condition', group: 'Settings & Config', permission: 'terms-condition' },
    { label: 'Ads', route: '/ads', group: 'Settings & Config', permission: 'ads' },
    { label: 'Sectors', route: '/sectors', group: 'Settings & Config', permission: 'sectors' },
    { label: 'Interests', route: '/interests', group: 'Settings & Config' },
    { label: 'Business Objective', route: '/business-objective', group: 'Settings & Config' },
    { label: 'Pages', route: '/setting/pages', group: 'Settings & Config', superAdmin: true },
    { label: 'Activity Logs', route: '/activity-logs', group: 'Settings & Config' },
    { label: 'Referral Activity Log', route: '/referral-config/activity-log', group: 'Settings & Config' },

    { label: 'Dashboard', route: '/loyalty/dashboard', group: 'Loyalty', loyalty: true, permission: 'loyalty-dashboard' },
    { label: 'System Config', route: '/loyalty/system-config', group: 'Loyalty', loyalty: true, permission: 'loyalty-system-config' },
    { label: 'Referral Config', route: '/referral-config', group: 'Loyalty', loyalty: true },
    { label: 'Providers', route: '/loyalty/providers', group: 'Loyalty', loyalty: true, permission: 'loyalty-providers' },
    { label: 'Country Config', route: '/loyalty/country-config', group: 'Loyalty', loyalty: true, permission: 'loyalty-country-config' },
    { label: 'System Budget', route: '/loyalty/system-budget', group: 'Loyalty', loyalty: true, permission: 'loyalty-country-config' },
    { label: 'Points Packages', route: '/loyalty/points-packages', group: 'Loyalty', loyalty: true, permission: 'loyalty-points-package' },
    { label: 'Marketplace', route: '/loyalty/marketplace', group: 'Loyalty', loyalty: true, permission: 'loyalty-marketplace' },
    { label: 'Finance', route: '/loyalty/finance', group: 'Loyalty', loyalty: true, permission: 'loyalty-finance' },
    { label: 'Campaign Surplus', route: '/loyalty/contest-surplus', group: 'Loyalty', loyalty: true, permission: 'loyalty-contest-surplus' },
    { label: 'Notifications', route: '/loyalty/notifications', group: 'Loyalty', loyalty: true, permission: 'loyalty-notification' },
    { label: 'User Management', route: '/loyalty/user-management', group: 'Loyalty', loyalty: true, permission: 'loyalty-user-management' },
    { label: 'Transaction Log', route: '/loyalty/transaction-log', group: 'Loyalty', loyalty: true, permission: 'loyalty-transaction-log' },
    { label: 'Clients Pool Ledger', route: '/loyalty/clients-pool-ledger', group: 'Loyalty', loyalty: true, permission: 'loyalty-clients-pool-ledger' },
    { label: 'Payment Logs', route: '/loyalty/payment-logs', group: 'Loyalty', loyalty: true, permission: 'loyalty-payment-logs' },
    { label: 'Audit Log', route: '/loyalty/audit-log', group: 'Loyalty', loyalty: true, permission: 'loyalty-audit-log' },

    { label: 'Super Admin Logs', route: '/super-admin-logs', superAdmin: true },
    { label: 'Profile', route: '/profile', permission: 'profile' },
  ];

  // Menu structure mapping routes to parent menus
  private menuRouteMapping: { [key: string]: string } = {
    '/categories': 'content',
    '/quiz': 'content',
    '/contests': 'content',
    '/task-management': 'content',
    '/users': 'users',
    '/clients': 'users',
    '/client-qr': 'users',
    '/branding': 'users',
    '/notification': 'communication',
    '/newsletter': 'communication',
    '/feedback': 'communication',
    '/request': 'communication',
    '/setting': 'settings',
    '/subscription': 'settings',
    '/terms-condition': 'settings',
    '/ads': 'settings',
    '/sectors': 'settings',
    '/business-objective': 'settings',
    '/email-templates': 'communication',
    '/loyalty': 'loyalty',
    '/super-admin-logs': 'super-admin-logs'
  };

  constructor(
    private authService: AuthService,
    private notificationService: NotificationsService,
    private notificationStateService: NotificationStateService,
    private permissionService: PermissionService,
    private realtimeService: RealtimeService,
    private secureStorage: SecureStorageService,
    private router: Router,
    private requestCountService: RequestCountService,
    private routePrefetch: RoutePrefetchService
  ) {
    // Load expanded menus from session storage in constructor
    this.expandedMenus = this.loadExpandedMenus();
    this.isSidebarCollapsed = this.loadSidebarCollapsed();
    this.sidebarScrollTop = this.loadSidebarScrollTop();
  }

  // Load expanded menus from session storage
  private loadExpandedMenus(): { [key: string]: boolean } {
    try {
      const saved = sessionStorage.getItem('expandedMenus');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  }

  // Save expanded menus to session storage
  private saveExpandedMenus(): void {
    try {
      sessionStorage.setItem('expandedMenus', JSON.stringify(this.expandedMenus));
    } catch {
      // Ignore storage errors
    }
  }

  private loadSidebarCollapsed(): boolean {
    try {
      return sessionStorage.getItem('isSidebarCollapsed') === 'true';
    } catch {
      return false;
    }
  }

  private saveSidebarCollapsed(): void {
    try {
      sessionStorage.setItem('isSidebarCollapsed', String(this.isSidebarCollapsed));
    } catch {
      // Ignore storage errors
    }
  }

  toggleSidebarCollapse(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
    this.saveSidebarCollapsed();
    this.hideSidebarTooltip();
    this.closeAllCollapsedFlyouts();
    if (!this.isSidebarCollapsed) {
      this.expandedMenus = {};
      this.saveExpandedMenus();
    }
  }

  toggleMobileSidebar(): void {
    this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    if (typeof document !== 'undefined') {
      document.body.style.overflow = this.isMobileSidebarOpen ? 'hidden' : '';
    }
  }

  closeMobileSidebar(): void {
    if (!this.isMobileSidebarOpen) return;
    this.isMobileSidebarOpen = false;
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  }

  @HostListener('document:keydown.escape')
  onEscapePressed(): void {
    this.closeMobileSidebar();
  }

  private loadSidebarScrollTop(): number {
    try {
      const raw = sessionStorage.getItem('sidebarScrollTop');
      const parsed = raw ? Number(raw) : 0;
      return Number.isFinite(parsed) ? parsed : 0;
    } catch {
      return 0;
    }
  }

  private saveSidebarScrollTop(): void {
    try {
      sessionStorage.setItem('sidebarScrollTop', String(this.sidebarScrollTop));
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Hovering a sidebar entry starts downloading its lazy bundle, so the click
   * that follows a second later opens the page immediately instead of waiting
   * for the chunk (see RoutePrefetchService). Delegated from the sidebar
   * container — mouseover bubbles, so one listener covers every link.
   */
  onSidebarHover(event: Event): void {
    const anchor = (event.target as HTMLElement | null)?.closest('a');
    const href = anchor?.getAttribute('href');
    if (href) this.routePrefetch.prioritize(href);
  }

  onSidebarScroll(event: Event): void {
    const el = event.target as HTMLElement | null;
    if (!el) return;
    this.sidebarScrollTop = el.scrollTop;
    this.saveSidebarScrollTop();

    this.isSidebarScrolling = true;
    if (this.scrollHideTimer) clearTimeout(this.scrollHideTimer);
    this.scrollHideTimer = setTimeout(() => {
      this.isSidebarScrolling = false;
    }, 800);
  }

  private restoreSidebarScrollTop(): void {
    if (!this.sidebarScrollContainer?.nativeElement) {
      return;
    }
    this.sidebarScrollContainer.nativeElement.scrollTop = this.sidebarScrollTop;
  }

  openCollapsedFlyout(menuKey: string): void {
    if (!this.isSidebarCollapsed) {
      return;
    }

    this.clearCollapsedHoverTimer();
    this.clearFlyoutStyles();
    this.collapsedHoverMenuKey = menuKey;
    // Reposition the flyout once Angular has rendered it so it always stays
    // fully inside the viewport (with an internal scrollbar when very tall).
    setTimeout(() => this.positionCollapsedFlyout(), 0);
  }

  /**
   * Keeps the collapsed-sidebar flyout submenu inside the viewport. The flyout
   * is absolutely positioned next to its parent item; when the parent sits low
   * on screen the flyout would overflow off the bottom, so we shift it up and
   * cap its height (CSS adds the scrollbar).
   */
  private positionCollapsedFlyout(): void {
    if (!this.isSidebarCollapsed) {
      return;
    }
    const flyout = document.querySelector(
      '.sidebar-container.collapsed .has-submenu .submenu.collapsed-flyout-open'
    ) as HTMLElement | null;
    if (!flyout) {
      return;
    }
    const parentLi = flyout.closest('.has-submenu') as HTMLElement | null;
    if (!parentLi) {
      return;
    }

    const margin = 8;
    const viewportHeight = window.innerHeight;
    const liRect = parentLi.getBoundingClientRect();

    // Cap the height to the viewport (CSS provides overflow-y: auto).
    flyout.style.maxHeight = `${viewportHeight - margin * 2}px`;
    const flyoutHeight = flyout.offsetHeight;

    // Default: align the flyout's top with the parent item.
    let viewportTop = liRect.top;
    // Shift up if it would overflow the bottom edge.
    if (viewportTop + flyoutHeight > viewportHeight - margin) {
      viewportTop = viewportHeight - margin - flyoutHeight;
    }
    // Never let it go above the top edge.
    if (viewportTop < margin) {
      viewportTop = margin;
    }

    // `top` is relative to the absolutely-positioned parent <li>.
    flyout.style.top = `${Math.round(viewportTop - liRect.top)}px`;
  }

  scheduleCloseCollapsedFlyout(): void {
    if (!this.isSidebarCollapsed) {
      return;
    }

    this.clearCollapsedHoverTimer();
    this.collapsedHoverCloseTimer = setTimeout(() => {
      this.closeAllCollapsedFlyouts();
    }, 180);
  }

  closeAllCollapsedFlyouts(): void {
    this.collapsedHoverMenuKey = null;
    this.clearCollapsedHoverTimer();
    this.clearFlyoutStyles();
  }

  private clearFlyoutStyles(): void {
    if (typeof document === 'undefined') return;
    const submenus = document.querySelectorAll(
      '.submenu'
    ) as NodeListOf<HTMLElement>;
    submenus.forEach(el => {
      el.style.maxHeight = '';
      el.style.top = '';
    });
  }

  isCollapsedFlyoutOpen(menuKey: string): boolean {
    return this.isSidebarCollapsed && this.collapsedHoverMenuKey === menuKey;
  }

  onSidebarMouseOver(event: MouseEvent): void {
    if (!this.isSidebarCollapsed) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const link = target.closest('a[data-tooltip]') as HTMLElement | null;
    if (!link) {
      return;
    }

    // Parent items with children use flyout submenu in collapsed mode.
    if (link.classList.contains('parent-menu')) {
      this.hideSidebarTooltip();
      return;
    }

    const text = link.getAttribute('data-tooltip');
    if (!text) {
      return;
    }

    const rect = link.getBoundingClientRect();
    this.sidebarTooltipText = text;
    this.sidebarTooltipLeft = rect.right + 12;
    this.sidebarTooltipTop = rect.top + rect.height / 2;
    this.sidebarTooltipVisible = true;
  }

  onSidebarMouseOut(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const related = event.relatedTarget as HTMLElement | null;
    if (!target) {
      this.hideSidebarTooltip();
      return;
    }

    const link = target.closest('a[data-tooltip]');
    if (!link) {
      return;
    }

    if (related && link.contains(related)) {
      return;
    }

    this.hideSidebarTooltip();
  }

  private hideSidebarTooltip(): void {
    this.sidebarTooltipVisible = false;
    this.sidebarTooltipText = '';
  }

  private clearCollapsedHoverTimer(): void {
    if (this.collapsedHoverCloseTimer) {
      clearTimeout(this.collapsedHoverCloseTimer);
      this.collapsedHoverCloseTimer = null;
    }
  }
  // Toggle submenu expansion
  toggleMenu(menuKey: string): void {
    if (this.isSidebarCollapsed) {
      // In collapsed mode: click toggles the flyout open/closed
      if (this.collapsedHoverMenuKey === menuKey) {
        this.closeAllCollapsedFlyouts();
      } else {
        this.openCollapsedFlyout(menuKey);
      }
      return;
    }

    // Allow multiple dropdown menus to be open simultaneously
    this.expandedMenus[menuKey] = !this.expandedMenus[menuKey];
    this.saveExpandedMenus();
  }

  /** True while the search box has something in it — the tree hides then. */
  get isMenuSearching(): boolean {
    return this.menuSearch.trim().length > 0;
  }

  /** Menu entries matching the search box, filtered by the same permissions
   *  the hand-written tree checks. Matches on the item label or its group. */
  get menuSearchResults(): Array<{
    label: string;
    route: string;
    group?: string;
    queryParams?: any;
  }> {
    const query = this.menuSearch.trim().toLowerCase();
    if (!query) return [];

    return this.searchableMenu.filter((item) => {
      if (item.superAdmin && !this.isSuperAdmin()) return false;
      if (item.loyalty && !this.hasLoyaltyAccess()) return false;
      if (item.permission && !this.hasPermission(item.permission)) return false;

      return (
        item.label.toLowerCase().includes(query) ||
        (item.group || '').toLowerCase().includes(query)
      );
    });
  }

  /** Navigate to a search hit, then put the sidebar back to its normal state. */
  goToMenuResult(item: { route: string; queryParams?: any }): void {
    this.router.navigate([item.route], { queryParams: item.queryParams });
    this.clearMenuSearch();
    this.closeMobileSidebar();
  }

  clearMenuSearch(): void {
    this.menuSearch = '';
  }

  // Check if menu is expanded - only use saved state (user's manual control)
  isMenuExpanded(menuKey: string): boolean {
    // Only return the saved state - respect user's manual expand/collapse
    return this.expandedMenus[menuKey] === true;
  }

  // Check if any child route of a parent menu is currently active
  isChildRouteActive(menuKey: string): boolean {
    const currentUrl = this.router.url.split('?')[0];

    for (const [route, key] of Object.entries(this.menuRouteMapping)) {
      if (key === menuKey && currentUrl.startsWith(route)) {
        return true;
      }
    }
    return false;
  }

  // Check if current URL matches any of the given route prefixes
  isAnyRouteActive(routes: string[]): boolean {
    const currentUrl = this.router.url.split('?')[0];
    return routes.some(route => currentUrl === route || currentUrl.startsWith(route + '/'));
  }

  // Auto-expand parent menu based on current route
  private expandMenuForCurrentRoute(): void {
    // Never auto-expand when sidebar is collapsed — menus stay closed until user manually opens them
    if (this.isSidebarCollapsed) {
      return;
    }

    const currentUrl = this.router.url.split('?')[0]; // Remove query params

    // Check if current route belongs to a parent menu and expand it
    for (const [route, menuKey] of Object.entries(this.menuRouteMapping)) {
      if (currentUrl.startsWith(route)) {
        this.expandedMenus[menuKey] = true;
        this.saveExpandedMenus();
        break;
      }
    }
  }

  ngOnInit() {
    const user = this.secureStorage.getObject<any>('user');
    const name = user?.name?.trim();
    this.userName = name ? name : 'Hi,DigiContests';
    this.userEmail = this.getUserEmail(user);
    this.userRole = this.getUserRole(user);
    this.userInitials = this.getUserInitials(name || this.userName);

    // Auto-expand menu for current route on initial load
    this.expandMenuForCurrentRoute();

    // Listen for route changes
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      // Always close the collapsed flyout on navigation
      this.closeAllCollapsedFlyouts();
      this.closeMobileSidebar();
      setTimeout(() => this.restoreSidebarScrollTop(), 0);
      setTimeout(() => this.restoreSidebarScrollTop(), 120);
    });

    this.loadNotificationHistory();
    this.loadNewRequestCount();
    // Subscribe to notification state changes
    this.notificationStateService.notificationRead$.subscribe(() => {
      this.loadNotificationHistory();
    });

    // The live channel: the gateway's own WebSocket, authenticated with this session. No key,
    // no cluster, no shared channel — and what arrives here was addressed to THIS admin.
    this.realtimeService.connect();

    // A notification for this admin: refresh the bell rather than wait for the next poll.
    this.realtimeService.notifications$.subscribe(() => {
      this.loadNotificationHistory();
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.restoreSidebarScrollTop(), 0);
  }

  ngOnDestroy() {
    this.clearCollapsedHoverTimer();
    if (this.scrollHideTimer) clearTimeout(this.scrollHideTimer);
    this.realtimeService.disconnect();
    this.requestCountSub?.unsubscribe();
  }

  logout(): void {
    this.authService.logout();
  }

  hasPermission(pageKey: string): boolean {
    const result = this.permissionService.hasPermission(pageKey);
    // console.log('Permission check:', pageKey, result);
    return result;
  }

  isSuperAdmin(): boolean {
    return this.permissionService.isSuperAdmin;
  }

  /** The Loyalty micro-service gateway requires `msAuthToken`, only issued
   *  on login for super_admin accounts (see LoyaltyAccessGuard) — an admin
   *  can be granted ordinary view permission for loyalty pages without ever
   *  having this token, which made the sidebar link a dead end that always
   *  errored. Hide it instead of leaving that trap for the user to find. */
  hasLoyaltyAccess(): boolean {
    return !!this.secureStorage.getItem('msAuthToken');
  }

  loadNotificationHistory(): void {
    this.notificationService.getNotificationUnread(true).subscribe({
      next: (data) => {
        if (typeof data === 'number') {
          this.notificationCount = data;
          return;
        }

        const list = Array.isArray(data)
          ? data
          : Array.isArray((data as any)?.items)
            ? (data as any).items
            : Array.isArray((data as any)?.data)
              ? (data as any).data
              : [];

        if (typeof (data as any)?.count === 'number') {
          this.notificationCount = (data as any).count;
        } else {
          this.notificationCount = list.length;
        }
      },
      error: (err) => {
        console.error('Error fetching unread notifications:', err);
        this.notificationCount = 0;
      },
    });
  }

  /** Sidebar badge = new requests across every Requests tab (not just Contact Us). */
  loadNewRequestCount(): void {
    if (!this.hasPermission('request')) return;
    this.requestCountSub ??= this.requestCountService.total$.subscribe((total) => {
      this.requestCount = total;
    });
    this.requestCountService.refresh();
  }

  handleLogout() {
    this.logoutModalVisible = true;
  }

  handleLogoutCancel() {
    this.logoutModalVisible = false;
  }

  handleLogoutConfirm() {
    this.logoutModalVisible = false;
    this.logout();
  }

  private getUserEmail(user: any): string {
    const direct = (user?.email || '').trim();
    if (direct) return direct;
    const google = (user?.google_oauth?.email || '').trim();
    if (google) return google;
    const apple = (user?.apple_oauth?.email || '').trim();
    if (apple) return apple;
    return 'operation@luqta.io';
  }

  private getUserRole(user: any): string {
    const role = (user?._roles?.name || user?.system_roles || '').toString().trim();
    // system_roles is a slug (e.g. "super_admin") — show it as a label.
    return role ? role.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) : 'Admin';
  }

  private getUserInitials(name: string): string {
    const cleaned = (name || '').trim();
    if (!cleaned) return 'U';
    const parts = cleaned.split(/\s+/).slice(0, 2);
    return parts.map((p) => p.charAt(0).toUpperCase()).join('');
  }
}

